class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productsToShow = parseInt(this.dataset.productsToShow) || 6;
    this.slider = this.querySelector('#Slider-' + this.sectionId);
    this.showDemoProducts = this.dataset.showDemoProducts === 'true';
    this.hideWhenEmpty = this.dataset.hideWhenEmpty === 'true';
    this.init();
  }

  init() {
    this.loadRecentlyViewedProducts();
    this.trackCurrentProduct();
  }

  trackCurrentProduct() {
    const currentProductHandle = this.getCurrentProductHandle();
    if (currentProductHandle) {
      this.addToRecentlyViewed(currentProductHandle);
    }
  }

  getCurrentProductHandle() {
    const productHandleElement = document.querySelector('[data-product-handle]');
    if (productHandleElement) {
      return productHandleElement.dataset.productHandle;
    }
    
    const pathParts = window.location.pathname.split('/').filter(part => part);
    if (pathParts.includes('products') && pathParts.length > 1) {
      return pathParts[pathParts.indexOf('products') + 1];
    }
    
    return null;
  }

  getRecentlyViewedProducts() {
    const recentlyViewed = localStorage.getItem('recentlyViewed');
    return recentlyViewed ? JSON.parse(recentlyViewed) : [];
  }

  addToRecentlyViewed(productHandle) {
    let recentlyViewed = this.getRecentlyViewedProducts();
    
    recentlyViewed = recentlyViewed.filter(handle => handle !== productHandle);
    recentlyViewed.unshift(productHandle);
    
    if (recentlyViewed.length > 20) {
      recentlyViewed = recentlyViewed.slice(0, 20);
    }
    
    localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
  }

  async loadRecentlyViewedProducts() {
    const recentlyViewed = this.getRecentlyViewedProducts();
    
    if (recentlyViewed.length === 0) {
      if (this.showDemoProducts) {
        await this.loadRealProductsForDemo();
      } else {
        this.showEmptyState();
      }
      return;
    }

    const productsToLoad = recentlyViewed.slice(0, this.productsToShow);
    const productPromises = productsToLoad.map(handle => this.fetchProductByHandle(handle));
    
    try {
      const products = await Promise.all(productPromises);
      const validProducts = products.filter(product => product !== null);
      
      if (validProducts.length === 0) {
        if (this.showDemoProducts) {
          await this.loadRealProductsForDemo();
        } else {
          this.showEmptyState();
        }
        return;
      }
      
      this.renderProducts(validProducts);
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      if (this.showDemoProducts) {
        await this.loadRealProductsForDemo();
      } else {
        this.showEmptyState();
      }
    }
  }

  async loadRealProductsForDemo() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}collections/all?sort_by=best-selling&limit=6`);
      if (!response.ok) {
        this.showDemoProductsList();
        return;
      }
      
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      
      const productLinks = doc.querySelectorAll('.card__heading a');
      if (productLinks.length === 0) {
        this.showDemoProductsList();
        return;
      }
      
      const productHandles = Array.from(productLinks).slice(0, this.productsToShow).map(link => {
        const href = link.getAttribute('href');
        return href ? href.replace('/products/', '') : null;
      }).filter(handle => handle);
      
      if (productHandles.length === 0) {
        this.showDemoProductsList();
        return;
      }
      
      const productPromises = productHandles.map(handle => this.fetchProductByHandle(handle));
      const products = await Promise.all(productPromises);
      const validProducts = products.filter(product => product !== null);
      
      if (validProducts.length === 0) {
        this.showDemoProductsList();
      } else {
        this.renderProducts(validProducts);
      }
    } catch (error) {
      console.error('Error loading real products for demo:', error);
      this.showDemoProductsList();
    }
  }

  async fetchProductByHandle(handle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) return null;

      const product = await response.json();

      if (!product) return null;

      return {
        id: product.id,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor || '',
        price: product.price != null ? product.price : '',
        compare_at_price: product.compare_at_price != null ? product.compare_at_price : '',
        featured_image: product.featured_image || (product.images && product.images[0]) || '',
        url: product.url || `/products/${product.handle}`
      };
    } catch (error) {
      console.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  }

  async fetchProductRecommendations(handle) {
    try {
      const response = await fetch(window.Shopify.routes.root + `recommendations/products?product_id=${handle}&limit=1&section_id=product-recommendations`);
      if (!response.ok) return null;
      
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      
      const productElement = doc.querySelector('.product-recommendations__item');
      if (productElement) {
        const productLink = productElement.querySelector('a');
        const productImage = productElement.querySelector('img');
        const productTitle = productElement.querySelector('.product-item__title');
        const productPrice = productElement.querySelector('.price');
        
        return {
          handle: handle,
          title: productTitle ? productTitle.textContent.trim() : '',
          price: productPrice ? productPrice.textContent.trim() : '',
          featured_image: productImage ? productImage.src : '',
          url: productLink ? productLink.href : `/products/${handle}`
        };
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching product recommendations for ${handle}:`, error);
      return null;
    }
  }

  renderProducts(products) {
    if (!this.slider) return;

    const sectionSettings = this.getSectionSettings();
    const gridHTML = products.map((product, index) => {
      return this.createProductCardHTML(product, index, sectionSettings);
    }).join('');

    this.slider.innerHTML = gridHTML;
    
    this.updateSliderCounter(products.length);
    this.toggleArrows(products.length);
    this.initSlider();
  }

  toggleArrows(productCount) {
    const columnsDesktop = parseInt(this.dataset.columnsDesktop) || 4;
    const columnsMobile = parseInt(this.dataset.columnsMobile) || 2;
    const isDesktop = window.innerWidth >= 750;
    const columns = isDesktop ? columnsDesktop : columnsMobile;
    
    const sliderButtons = this.querySelector('.slider-buttons');
    if (!sliderButtons) return;

    if (productCount > columns) {
      sliderButtons.classList.remove('hidden');
    } else {
      sliderButtons.classList.add('hidden');
    }
  }

  getSectionSettings() {
    const section = document.getElementById(`recently-viewed-${this.sectionId}`);
    if (!section) return {};

    return {
      showSecondaryImage: section.dataset.showSecondaryImage === 'true',
      showVendor: section.dataset.showVendor === 'true',
      showRating: section.dataset.showRating === 'true',
      imageRatio: section.dataset.imageRatio || 'adapt',
      cardStyle: section.dataset.cardStyle || 'standard',
      cardColorScheme: section.dataset.cardColorScheme || 'scheme-2'
    };
  }

  createProductCardHTML(product, index, settings) {
    const imageUrl = product.featured_image || '';
    const price = product.price || '';
    const compareAtPrice = product.compare_at_price || '';
    const hasComparePrice = compareAtPrice && compareAtPrice > price;
    const formattedPrice = this.formatMoney(price);
    const formattedComparePrice = hasComparePrice ? this.formatMoney(compareAtPrice) : '';

    let aspect = 1;
    if (settings.imageRatio === 'portrait') {
      aspect = 0.8;
    } else if (settings.imageRatio === 'landscape') {
      aspect = 1.33;
    } else if (settings.imageRatio === 'adapt' && product.featured_media && product.featured_media.aspect_ratio) {
      aspect = product.featured_media.aspect_ratio;
    }
    const ratioPercent = (1 / aspect) * 100;
    const cardSchemeClass = settings.cardStyle === 'card' ? `color-${settings.cardColorScheme} gradient` : '';
    const innerSchemeClass = settings.cardStyle === 'standard' ? `color-${settings.cardColorScheme} gradient` : '';

    return `
      <li
        id="Slide-${this.sectionId}-${index}"
        class="grid__item"
        data-cascade
        style="--animation-order: ${index};"
      >
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--${settings.cardStyle} card--media ${cardSchemeClass}">
            <div class="card__inner ${innerSchemeClass} ratio" style="--ratio-percent: ${ratioPercent}%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <img
                    src="${imageUrl}"
                    alt="${product.title}"
                    class="motion-reduce"
                    loading="lazy"
                    width="400"
                    height="400"
                  >
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5">
                    <a href="${product.url}" class="full-unstyled-link">
                      ${product.title}
                    </a>
                  </h3>
                  ${settings.showVendor ? `<div class="card__vendor caption-with-letter-spacing light">${product.vendor || ''}</div>` : ''}
                  ${settings.showRating ? '<div class="jdgm-widget jdgm-preview-badge" data-id="' + product.id + '"></div>' : ''}
                </div>
                <div class="card__badge top left"></div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${product.title}
                  </a>
                </h3>
                ${settings.showVendor ? `<div class="card__vendor caption-with-letter-spacing light">${product.vendor || ''}</div>` : ''}
                ${settings.showRating ? '<div class="jdgm-widget jdgm-preview-badge" data-id="' + product.id + '"></div>' : ''}
                <div class="price ${hasComparePrice ? 'price--on-sale' : ''}" role="group">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">Regular price</span>
                      <span class="price-item price-item--regular">${formattedPrice}</span>
                    </div>
                    ${hasComparePrice ? `
                      <div class="price__sale">
                        <span class="visually-hidden visually-hidden--inline">Sale price</span>
                        <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
                      </div>
                      <div class="price__compare">
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                        <span class="price-item price-item--regular price-item--compare">${formattedComparePrice}</span>
                      </div>
                    ` : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  formatMoney(cents) {
    if (cents === '' || cents === null || cents === undefined) return '';
    const amountNumber = parseFloat(cents) / 100;
    if (isNaN(amountNumber)) return String(cents);

    const moneyFormat = this.dataset.moneyFormat || 'R {{ amount }}';
    const noDecimals = /no_decimals/.test(moneyFormat);
    const amount = amountNumber
      .toFixed(noDecimals ? 0 : 2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
  }

  showEmptyState() {
    if (this.hideWhenEmpty) {
      this.classList.add('recently-viewed--hidden');
      return;
    }

    if (!this.slider) return;

    this.slider.innerHTML = `
      <div class="recently-viewed-empty">
        <p>No recently viewed products yet. Start browsing and your viewed items will appear here.</p>
      </div>
    `;
  }

  showDemoProductsList() {
    if (!this.slider) return;

    const demoProducts = this.getDemoProducts();
    this.renderProducts(demoProducts);
  }

  getDemoProducts() {
    return [
      {
        handle: 'demo-product-1',
        title: 'Fitness Equipment',
        price: '£299.00',
        compare_at_price: '',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      },
      {
        handle: 'demo-product-2',
        title: 'Home Gym Setup',
        price: '£599.00',
        compare_at_price: '£799.00',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      },
      {
        handle: 'demo-product-3',
        title: 'Training Accessories',
        price: '£49.00',
        compare_at_price: '',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      },
      {
        handle: 'demo-product-4',
        title: 'Cardio Machine',
        price: '£899.00',
        compare_at_price: '',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      },
      {
        handle: 'demo-product-5',
        title: 'Strength Equipment',
        price: '£449.00',
        compare_at_price: '£599.00',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      },
      {
        handle: 'demo-product-6',
        title: 'Yoga Mat',
        price: '£29.00',
        compare_at_price: '',
        featured_image: 'https://cdn.shopify.com/s/files/1/0533/2089/files/product-placeholder.jpg',
        url: '#'
      }
    ];
  }

  updateSliderCounter(total) {
    const counterCurrent = this.querySelector('.slider-counter--current');
    const counterTotal = this.querySelector('.slider-counter--total');
    
    if (counterTotal) {
      counterTotal.textContent = total;
    }
    if (counterCurrent) {
      counterCurrent.textContent = '1';
    }
  }

  initSlider() {
    const sliderComponent = this.querySelector('slider-component');
    if (sliderComponent) {
      if (typeof sliderComponent.init === 'function') {
        sliderComponent.init();
      }
      if (sliderComponent.slider && typeof sliderComponent.resetPages === 'function') {
        sliderComponent.resetPages();
      }
    }

    window.addEventListener('resize', () => {
      this.toggleArrows(parseInt(this.querySelector('.slider-counter--total')?.textContent || 0));
    });
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}

document.addEventListener('DOMContentLoaded', () => {
  const recentlyViewedSections = document.querySelectorAll('[id^="recently-viewed-"]');
  recentlyViewedSections.forEach(section => {
    if (!section.classList.contains('recently-viewed')) {
      section.classList.add('recently-viewed');
    }
  });
});
